/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 **************************************************************************/
export var SAMErrorCode;
(function (SAMErrorCode) {
    SAMErrorCode[SAMErrorCode["UNKNOWN"] = 1] = "UNKNOWN";
    SAMErrorCode[SAMErrorCode["FULL_LOCAL_DISK_SPACE"] = 2] = "FULL_LOCAL_DISK_SPACE";
    SAMErrorCode[SAMErrorCode["FULL_CLOUD_STORAGE"] = 3] = "FULL_CLOUD_STORAGE";
    SAMErrorCode[SAMErrorCode["DEVICE_OFFLINE"] = 4] = "DEVICE_OFFLINE";
    SAMErrorCode[SAMErrorCode["NETWORK_ERROR"] = 5] = "NETWORK_ERROR";
    SAMErrorCode[SAMErrorCode["REQUEST_TIMED_OUT"] = 6] = "REQUEST_TIMED_OUT";
    SAMErrorCode[SAMErrorCode["USER_CANCELLED"] = 7] = "USER_CANCELLED";
    SAMErrorCode[SAMErrorCode["CORESYNC_FAILURE"] = 8] = "CORESYNC_FAILURE";
    /**
     * CoreSync is in a paused state.
     */
    SAMErrorCode[SAMErrorCode["SYNC_PAUSED"] = 9] = "SYNC_PAUSED";
    SAMErrorCode[SAMErrorCode["USER_ALREADY_REGISTERED"] = 10] = "USER_ALREADY_REGISTERED";
    SAMErrorCode[SAMErrorCode["PENDING_UNCOMMITTED_CHANGES_FROM_THIS_APP"] = 11] = "PENDING_UNCOMMITTED_CHANGES_FROM_THIS_APP";
    SAMErrorCode[SAMErrorCode["PENDING_UNCOMMITTED_CHANGES_FROM_ANOTHER_APP"] = 12] = "PENDING_UNCOMMITTED_CHANGES_FROM_ANOTHER_APP";
    SAMErrorCode[SAMErrorCode["INTERNAL_ERROR"] = 13] = "INTERNAL_ERROR";
    SAMErrorCode[SAMErrorCode["OPERATION_IGNORED"] = 14] = "OPERATION_IGNORED";
    /**
     * Indicates that the request could not be processed because of conflict
     * in the current state of the resource.
     * <br>Client Action: Resolve conflict and try again.
     */
    SAMErrorCode[SAMErrorCode["CONFLICT"] = 15] = "CONFLICT";
    /**
     * Happens when interacting with an asset the user no longer has access to.
     */
    SAMErrorCode[SAMErrorCode["ACCESS_DENIED"] = 16] = "ACCESS_DENIED";
    SAMErrorCode[SAMErrorCode["UNSUPPORTED"] = 17] = "UNSUPPORTED";
    SAMErrorCode[SAMErrorCode["ASSET_NOT_IN_SYNC"] = 18] = "ASSET_NOT_IN_SYNC";
    SAMErrorCode[SAMErrorCode["BAD_REQUEST"] = 19] = "BAD_REQUEST";
    /**
     * Happens if asset failed to sync.
     * There are associated sub-codes relating to this error which may have
     * more information
     */
    SAMErrorCode[SAMErrorCode["SYNC_ERROR"] = 20] = "SYNC_ERROR";
    /**
     * Happens if requests is made for an asset that cannot be found.
     */
    SAMErrorCode[SAMErrorCode["NOT_FOUND"] = 21] = "NOT_FOUND";
    /**
     * Happens when there is authorization error while performing the request
     * for e.g. if requests are made which require user to be registered with
     * ACPL, but user is not registered.
     */
    SAMErrorCode[SAMErrorCode["AUTHORIZATION_ERROR"] = 22] = "AUTHORIZATION_ERROR";
    /**
     * Happens while performing an invalid operation for e.g. when attempting
     * to migrate an asset to RepoNaming that has already been migrated
     */
    SAMErrorCode[SAMErrorCode["INVALID_OPERATION"] = 23] = "INVALID_OPERATION";
    /**
     * Happens when a method is called on a asset that require that the
     * asset have a context, but none is present or the asset does not
     * belong to specified context.
     */
    SAMErrorCode[SAMErrorCode["INVALID_CONTEXT"] = 24] = "INVALID_CONTEXT";
    /**
     * An error occured during committing changes.
     * Error sub-code may have more information around this
     */
    SAMErrorCode[SAMErrorCode["COMMIT_ERROR"] = 25] = "COMMIT_ERROR";
    /**
     *  Parent not found for the asset
     */
    SAMErrorCode[SAMErrorCode["PARENT_NOT_FOUND"] = 26] = "PARENT_NOT_FOUND";
    /**
     * Parent folder is discarded
     */
    SAMErrorCode[SAMErrorCode["PARENT_DISCARDED"] = 27] = "PARENT_DISCARDED";
    /**
     * Project limit exceeded
     */
    SAMErrorCode[SAMErrorCode["PROJECT_LIMIT_EXCEEDED"] = 28] = "PROJECT_LIMIT_EXCEEDED";
    /**
     * The user or organization is not provisioned
     * for the requested service access.
     */
    SAMErrorCode[SAMErrorCode["SERVICE_NOT_PROVISIONED"] = 29] = "SERVICE_NOT_PROVISIONED";
})(SAMErrorCode || (SAMErrorCode = {}));
export var SAMErrorSubCode;
(function (SAMErrorSubCode) {
    SAMErrorSubCode[SAMErrorSubCode["NONE"] = 0] = "NONE";
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_NOT_REACHABLE"] = 1] = "CORESYNC_NOT_REACHABLE";
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_NOT_COMPATIBLE"] = 2] = "CORESYNC_NOT_COMPATIBLE";
    /**
     * Happens if CoreSync binary is not present.  Ask end user install coresync
     */
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_BINARY_NOT_FOUND"] = 3] = "CORESYNC_BINARY_NOT_FOUND";
    /**
     * Happens if CoreSync is not installed. Ask end user to install/upgrade coresync.
     */
    SAMErrorSubCode[SAMErrorSubCode["CORESYNC_NOT_INSTALLED"] = 4] = "CORESYNC_NOT_INSTALLED";
    /**
     * Happens if asset failed to sync. Sync will be retried by ACPL.
     * Cient may wait for subsequent sync notification for the asset or retry
     * the operation.
     */
    SAMErrorSubCode[SAMErrorSubCode["SYNC_FAILED"] = 5] = "SYNC_FAILED";
    /**
     * Syncing the asset failed due to an EditEdit conflict, but the conflict
     * was resolved
     */
    SAMErrorSubCode[SAMErrorSubCode["SYNC_FAILED_EDIT_EDIT_RESOLVED"] = 6] = "SYNC_FAILED_EDIT_EDIT_RESOLVED";
    /**
     * Syncing the asset failed due to an EditDelete conflict, but the conflict
     * was resolved
     */
    SAMErrorSubCode[SAMErrorSubCode["SYNC_FAILED_EDIT_DELETE_RESOLVED"] = 7] = "SYNC_FAILED_EDIT_DELETE_RESOLVED";
    /**
     * An error occured while moving an orphan to the root folder.
     */
    SAMErrorSubCode[SAMErrorSubCode["COMMIT_ON_ORPHAN_ERROR"] = 8] = "COMMIT_ON_ORPHAN_ERROR";
    /**
     * Admin disabled creation of Projects
     * https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=assetscollab&title=Proposal%3A+Project+API+V3+-+Capability
     */
    SAMErrorSubCode[SAMErrorSubCode["PROJECTS_ADMIN_ONLY"] = 9] = "PROJECTS_ADMIN_ONLY";
    /**
     * 404 specific projects errors.
     * https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=assetscollab&title=Proposal%3A+Project+API+V3+-+Capability
     */
    SAMErrorSubCode[SAMErrorSubCode["PROJECTS_INVALID_REPOSITORY"] = 10] = "PROJECTS_INVALID_REPOSITORY";
    SAMErrorSubCode[SAMErrorSubCode["PROJECTS_NO_HOME_REPOSITORY"] = 11] = "PROJECTS_NO_HOME_REPOSITORY";
})(SAMErrorSubCode || (SAMErrorSubCode = {}));
export var ProblemType;
(function (ProblemType) {
    ProblemType["LIMIT_CHANGELOGRETENTION"] = "http://ns.adobe.com/adobecloud/problem/limit/changelogretention";
    ProblemType["OPERATION_FAILED"] = "adobecloud/problem/operation_failed";
    ProblemType["SUBSCRIPTION_COUNT"] = "http://ns.adobe.com/adobecloud/problem/limit/subscriptioncount";
    ProblemType["QUOTA_EXCEEDED"] = "http://ns.adobe.com/adobecloud/problem/quotaexceeded";
    ProblemType["CHILDREN_LIMIT_EXCEEDED"] = "http://ns.adobe.com/adobecloud/problem/limit/childrencount";
    ProblemType["NO_HOME_REPOSITORY"] = "http://ns.adobe.com/projects/problem/no-home-repository";
    ProblemType["INVALID_HOME_REPOSITORY"] = "http://ns.adobe.com/projects/problem/invalid-repository";
    ProblemType["ADMIN_ONLY"] = "http://ns.adobe.com/projects/problem/admin-only";
    ProblemType["ACCESS_DENIED"] = "http://ns.adobe.com/adobecloud/problem/accessdenied";
})(ProblemType || (ProblemType = {}));
export const SAMErrorName = 'SAMError';
